import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import {
  NdoePage, NdoePageHeader, NdoeLoading, NdoeError, NdoeActionButton,
} from '../../components/ndoe';
import { messageErreur } from '../../components/ndoe/chargement';
import { subscribeRealtime } from '../../realtime';
import './centrePilotage.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

/*
 * CENTRE DE PILOTAGE — une COMPOSITION, pas une nouvelle source de vérité.
 *
 * ── CE QUE CET ÉCRAN EST, ET CE QU'IL N'EST PAS ─────────────────────────────
 *
 * Il ne fait QU'UN SEUL appel : `GET /api/dashboard/admin`, celui que l'Accueil
 * utilise déjà. Aucune route n'a été créée pour lui, aucun calcul n'est refait
 * ici. C'est délibéré et c'est la condition de son existence.
 *
 * POURQUOI CETTE CONTRAINTE. Neuf des dix blocs demandés existaient déjà
 * ailleurs (audit du 22/08/2026) : ce que ce chantier apporte est une
 * HIÉRARCHIE — l'argent en premier, les alertes après — pas une capacité
 * nouvelle. Or une troisième surface qui recalculerait les mêmes chiffres est
 * exactement la manière dont une définition dérive.
 *
 * Ce n'est pas une crainte abstraite : la veille, « Attendu » affichait des
 * écarts NÉGATIFS parce que deux colonnes comptaient deux populations
 * différentes — et le défaut vivait au même endroit dans DEUX écrans. Une
 * troisième copie en aurait fait trois.
 *
 * ── LE X / Y DU HÉROS ───────────────────────────────────────────────────────
 *
 * « Commandes confirmées X / Y » n'existait pas comme paire sûre. Le fabriquer
 * avec les champs présents aurait produit un chiffre faux : `commandes_confirmees`
 * est un ENCOURS (ouvert maintenant), `commandes.jour` compte les CRÉATIONS du
 * jour. Les deux champs ont été ajoutés au service PARTAGÉ (`jour.commandes_*`),
 * sur une seule population — les commandes créées aujourd'hui — pour que le
 * rapport ne puisse ni dépasser 100 % ni devenir négatif.
 *
 * ── LE POINT DE CONNEXION ───────────────────────────────────────────────────
 *
 * Le dossier l'exige : « ne jamais afficher un faux point vert ». `realtime.js`
 * n'expose pas l'état du flux — seulement l'abonnement. On n'affiche donc PAS
 * un état de connexion qu'on ne connaît pas : on affiche l'HEURE de la dernière
 * lecture réussie et son âge, qui sont des faits observés. Le point ne passe au
 * vert qu'après un événement réellement reçu.
 */

const usd = (n) => `${Math.round(Number(n) || 0).toLocaleString('fr-CD')} $`;
const nb = (n) => (Number(n) || 0).toLocaleString('fr-CD');
// `0` n'est pas une donnée absente (règle du dossier) : une valeur nulle ou
// indéfinie s'affiche « — », jamais « 0 ».
const ou = (v, rendu) => (v === null || v === undefined ? '—' : rendu(v));

const CentrePilotage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [luA, setLuA] = useState(null);
  const [direct, setDirect] = useState(false);
  const [age, setAge] = useState(0);
  const enVolRef = useRef(false);

  const charger = useCallback(async (silencieux = false) => {
    if (enVolRef.current) return;
    enVolRef.current = true;
    if (!silencieux) { setChargement(true); setErreur(null); }
    try {
      const { data: d } = await axios.get(`${DOMAIN}/api/dashboard/admin`);
      setData(d);
      setLuA(new Date());
      setErreur(null);
    } catch (e) {
      // Un rafraîchissement silencieux qui échoue ne doit PAS effacer un écran
      // qui affiche encore des chiffres valides : on garde la vue et on laisse
      // l'âge vieillir, ce qui se voit.
      if (!silencieux) setErreur(messageErreur(e, 'le centre de pilotage'));
    } finally {
      enVolRef.current = false;
      if (!silencieux) setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  // Temps réel : on RÉUTILISE le flux existant, on n'en crée pas un second.
  // Une rafale d'événements ne déclenche qu'une relecture (coalescence 800 ms) :
  // sans cela, une grosse journée ferait autant d'appels que d'événements.
  useEffect(() => {
    let minuteur = null;
    const unsub = subscribeRealtime(() => {
      setDirect(true);
      clearTimeout(minuteur);
      minuteur = setTimeout(() => charger(true), 800);
    });
    return () => { clearTimeout(minuteur); unsub(); };
  }, [charger]);

  // L'âge de la dernière lecture — un fait, pas une promesse de connexion.
  useEffect(() => {
    const t = setInterval(() => {
      setAge(luA ? Math.round((Date.now() - luA.getTime()) / 1000) : 0);
    }, 5000);
    return () => clearInterval(t);
  }, [luA]);

  const ageTexte = !luA ? 'jamais lu'
    : age < 60 ? `mis à jour il y a ${age} s`
      : `mis à jour à ${luA.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
  // Vert seulement si un événement est réellement arrivé ET que la lecture est
  // fraîche. Au-delà de 3 minutes, on n'affirme plus rien : orange.
  const ton = !luA ? 'gris' : (direct && age < 180 ? 'vert' : 'orange');

  if (chargement) return <NdoePage><NdoeLoading rows={8} /></NdoePage>;
  if (erreur) {
    return (
      <NdoePage>
        <NdoeError title="Le centre de pilotage n’a pas pu être chargé" text={erreur} onRetry={() => charger()} />
      </NdoePage>
    );
  }
  if (!data) return null;

  const { hero = {}, jour = {}, temps_reel: tr = {}, funnel = {}, stock = {}, health = {}, alerts = [], balance_agee: bal = {} } = data;
  const enLiv = tr.en_livraison || {};
  const flu = tr.fluidite || {};
  const cmd = tr.commandes || {};
  const livreurs = (tr.par_livreur && tr.par_livreur.livreurs) || [];
  const relancer = (bal.a_relancer || []).slice(0, 5);

  const tuile = (valeur, libelle, route) => (
    <button type="button" key={libelle} className="cp-tuile" onClick={() => route && navigate(route)} disabled={!route}>
      <b className="num">{valeur}</b>
      <span>{libelle}</span>
    </button>
  );

  return (
    <NdoePage className="cp">
      <NdoePageHeader
        eyebrow="Pilotage"
        title="Centre de pilotage"
        subtitle="Vue d’essai — les chiffres viennent des mêmes calculs que l’Accueil"
        actions={(
          <span className={`cp-etat cp-etat--${ton}`}>
            <i />{direct ? 'Direct' : 'Lecture'} · {ageTexte}
          </span>
        )}
      />

      {/* ① LE HÉROS FINANCIER — décision produit : le montant d'abord, en grand,
          au-dessus de tout le reste. Les alertes ne passent jamais devant. */}
      <section className="cp-hero">
        <span className="cp-hero__label">Ventes confirmées</span>
        <b className="cp-hero__montant num">{usd(hero.ca_net_jour)}</b>
        <div className="cp-hero__sous">
          <span>Encaissé <b className="num">{usd(jour.encaisse_jour)}</b></span>
          <span>
            Commandes conclues{' '}
            <b className="num">
              {ou(jour.commandes_total, () => `${nb(jour.commandes_conclues)} / ${nb(jour.commandes_total)}`)}
            </b>
          </span>
          {hero.ca_net_hier != null && (
            <span className={`cp-hero__delta ${Number(hero.variation_pct) < 0 ? 'is-bas' : 'is-haut'}`}>
              {Number(hero.variation_pct) >= 0 ? '+' : ''}{hero.variation_pct} % vs hier ({usd(hero.ca_net_hier)})
            </span>
          )}
        </div>
        {hero.phrase && <p className="cp-hero__phrase">{hero.phrase}</p>}
      </section>

      {/* ② À TRAITER — après le héros, jamais avant. */}
      <section className="cp-bloc">
        <h3>À traiter</h3>
        <div className="cp-tuiles">
          {tuile(nb(cmd.sans_livreur), 'sans livreur', '/listeCommande')}
          {tuile(nb(flu.en_retard), 'en retard', '/supervision-livraisons')}
          {tuile(nb(flu.reports_7j), 'reports 7 j', '/supervision-livraisons')}
          {tuile(nb(health.ruptures), 'ruptures', '/rapports/ruptures')}
          {tuile(nb(health.faible_stock), 'stock faible', '/rapports/ruptures')}
        </div>
        {alerts.length > 0 && (
          <ul className="cp-alertes">
            {alerts.slice(0, 4).map((a, i) => (
              <li key={i}>{a.message || a.libelle || JSON.stringify(a)}</li>
            ))}
          </ul>
        )}
      </section>

      {/* ③ LE FLUX — l'entonnoir existant, rendu en une ligne lisible. */}
      <section className="cp-bloc">
        <h3>Flux des commandes</h3>
        <div className="cp-flux">
          <span><b className="num">{nb(funnel.commandes_actives)}</b> actives</span>
          <i>→</i>
          <span><b className="num">{nb(funnel.affectees)}</b> affectées</span>
          <i>→</i>
          <span><b className="num">{nb(funnel.prises_en_charge)}</b> prises en charge</span>
          <i>→</i>
          <span><b className="num">{nb(funnel.en_cours)}</b> en cours</span>
          <i>→</i>
          <span><b className="num">{nb(funnel.livrees_30j)}</b> livrées (30 j)</span>
        </div>
        <p className="cp-note">
          Taux de livraison {ou(flu.taux_livraison_pct, (v) => `${v} %`)} ·
          {' '}prise en charge {ou(flu.prise_en_charge_h, (v) => `${v} h`)} ·
          {' '}livraison {ou(flu.livraison_h, (v) => `${v} h`)}
        </p>
      </section>

      {/* ④ ENCOURS — ce qui est ouvert MAINTENANT, même créé un autre jour.
          Distinct de l'activité du jour : le dossier insiste, et c'est la
          confusion exacte qui a produit les écarts négatifs de la veille. */}
      <section className="cp-bloc">
        <h3>Encours <small>ouvert en ce moment</small></h3>
        <div className="cp-tuiles">
          {tuile(nb(enLiv.paires_confirmees), 'paires confirmées', '/supervision-livraisons')}
          {tuile(nb(enLiv.paires_affectees), 'affectées', '/supervision-livraisons')}
          {tuile(nb(enLiv.paires_reportees), 'reportées', '/supervision-livraisons')}
          {tuile(nb(jour.paires_essai), 'à l’essai', '/listeCommande')}
          {/* `/centreEchanges`, PAS `/centre-echanges` : la route est en casse
              chameau (App.js). Avec le tiret, la tuile menait à la page « 404 »
              depuis sa mise en service — les huit autres liens de cet écran ont
              été revérifiés un par un contre les routes déclarées. */}
          {tuile(nb(funnel.echanges_ouverts), 'échanges ouverts', '/centreEchanges')}
        </div>
      </section>

      <div className="cp-deux">
        {/* ⑤ ÉQUIPE LIVRAISON — instantané, sans nouveau score inventé. */}
        <section className="cp-bloc">
          <h3>Équipe livraison</h3>
          {!livreurs.length ? <p className="cp-vide">Aucun livreur en activité.</p> : (
            <ul className="cp-livreurs">
              {livreurs.slice(0, 6).map((l) => (
                <li key={l.id_user || l.id || l.username}>
                  <span className="cp-livreur__nom">{l.username || l.nom || '—'}</span>
                  <span className="num">{nb(l.paires_dehors)} dehors</span>
                  <span className="num">{usd(l.ca)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* ⑥ CLIENTES À SUIVRE — les créances les plus anciennes d'abord. */}
        <section className="cp-bloc">
          <h3>Clientes à relancer</h3>
          {!relancer.length ? <p className="cp-vide">Aucune relance en attente.</p> : (
            <ul className="cp-clientes">
              {relancer.map((c) => (
                <li key={c.id_client}>
                  <button type="button" onClick={() => navigate('/creances')}>
                    <span>{c.client}</span>
                    <span className="num">{usd(c.montant ?? c.solde)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {bal.total && (
            <p className="cp-note">
              {nb(bal.total.n)} créance(s) ouverte(s) · <b className="num">{usd(bal.total.montant)}</b>
            </p>
          )}
        </section>
      </div>

      {/* ⑦ STOCK À DÉCIDER — deep-link vers les filtres de découverte livrés
          ce matin, qui répondent précisément à « quoi brader ». */}
      <section className="cp-bloc">
        <h3>Stock à décider</h3>
        <div className="cp-tuiles">
          {tuile(nb(stock.disponible), 'paires disponibles', '/products')}
          {tuile(nb(stock.en_circulation), 'en circulation', '/supervision-livraisons')}
          {tuile(nb(health.ruptures), 'ruptures', '/rapports/ruptures')}
          {tuile(nb(health.faible_stock), 'stock faible', '/rapports/rotation')}
          {tuile('→', 'ce qui ne se vend pas', '/promotions')}
        </div>
      </section>

      {/* ⑧ ACTIONS RAPIDES — que des écrans existants. */}
      <section className="cp-bloc cp-actions">
        <NdoeActionButton variant="primary" onClick={() => navigate('/commandeForm')}>Nouvelle commande</NdoeActionButton>
        <NdoeActionButton variant="secondary" onClick={() => navigate('/supervision-livraisons')}>Supervision</NdoeActionButton>
        <NdoeActionButton variant="secondary" onClick={() => navigate('/promotions')}>Promotions</NdoeActionButton>
        <NdoeActionButton variant="secondary" onClick={() => navigate('/creances')}>Créances</NdoeActionButton>
        <NdoeActionButton variant="ghost" onClick={() => charger()}>Rafraîchir</NdoeActionButton>
      </section>

      <p className="cp-pied">
        Vue expérimentale. L’Accueil et « En direct » restent inchangés — cet écran
        lit exactement les mêmes calculs.
      </p>
    </NdoePage>
  );
};

export default CentrePilotage;
